import type { Store } from 'react-admin'
import { RA_STORE_PREFIX } from '../storeConfig'

/**
 * Store wrapper that keeps SPECIFIC keys tab-local (sessionStorage) while
 * delegating every other key to the wrapped app-global store (localStorage).
 *
 * Why: `selectedDeviceId` is the app's device-selection state. ra-core's
 * localStorageStore persists it to localStorage AND mirrors every write across
 * tabs via the `storage` event. That cross-tab mirroring is what caused the
 * multi-tab "pick a specific sensor, it bounces back to All Devices" thrash
 * (prod 2026-07): two tabs on different pets each run PetGlucoseDetailPanel's
 * convergence effect, which rewrites a foreign selection to its own pet's
 * default — so every tab clobbers the others' selection in an endless loop. A
 * concrete selection made in one tab is reverted within a frame.
 *
 * Routing the selection to sessionStorage makes it per-tab: sessionStorage is
 * NOT shared between tabs and does NOT fire cross-tab `storage` events, so tabs
 * can no longer see or overwrite each other's selection. Same-tab reactivity is
 * preserved via a local publish (below), identical to how the inner store
 * notifies useStore subscribers. Selection still survives a reload within the
 * same tab (sessionStorage), just not across tabs or a browser restart — which
 * is the correct lifetime for ephemeral "which sensor am I reviewing" state.
 *
 * Deliberately a single injection point (App.tsx) so NO call site changes:
 * every `useStore('selectedDeviceId')` keeps working unchanged; only the
 * backing storage + cross-tab behavior differs. Tests are unaffected — they
 * provide their own store via AdminContext and never use this wrapper.
 */

const tryParse = (value: string): any => {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

// sessionStorage is per-tab and wiped on tab close. In the rare environment
// where it's unavailable (some privacy modes), fall back to an in-memory map —
// still tab-local (this JS realm only), never cross-tab, which is all we need.
const memoryFallback = new Map<string, string>()
const sessionAvailable = (() => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false
    const k = '__vetpo_ss_test__'
    window.sessionStorage.setItem(k, '1')
    window.sessionStorage.removeItem(k)
    return true
  } catch {
    return false
  }
})()
const ssGet = (key: string): string | null =>
  sessionAvailable ? window.sessionStorage.getItem(key) : (memoryFallback.get(key) ?? null)
const ssSet = (key: string, value: string): void => {
  if (sessionAvailable) window.sessionStorage.setItem(key, value)
  else memoryFallback.set(key, value)
}
const ssRemove = (key: string): void => {
  if (sessionAvailable) window.sessionStorage.removeItem(key)
  else memoryFallback.delete(key)
}

export const perTabKeyStore = (
  inner: Store,
  tabLocalKeys: string[],
  prefix: string = RA_STORE_PREFIX,
): Store => {
  const isTabLocal = (key: string) => tabLocalKeys.includes(key)
  const storageKey = (key: string) => `${prefix}.${key}`

  // Same-tab pub/sub for the tab-local keys, mirroring localStorageStore's own
  // publish() so useStore subscribers re-render on setItem/removeItem/reset.
  const subscriptions: { [id: string]: { key: string; callback: (value: any) => void } } = {}
  const publishLocal = (key: string, value: any) => {
    Object.keys(subscriptions).forEach((id) => {
      if (subscriptions[id]?.key === key) subscriptions[id].callback(value)
    })
  }

  return {
    setup: () => inner.setup(),
    teardown: () => inner.teardown(),

    getItem<T = any>(key: string, defaultValue?: T): T {
      if (!isTabLocal(key)) return inner.getItem<T>(key, defaultValue) as T
      const raw = ssGet(storageKey(key))
      return (raw == null ? defaultValue : tryParse(raw)) as T
    },

    setItem<T = any>(key: string, value: T): void {
      if (!isTabLocal(key)) {
        inner.setItem(key, value)
        return
      }
      if (value === undefined) ssRemove(storageKey(key))
      else ssSet(storageKey(key), JSON.stringify(value))
      publishLocal(key, value)
    },

    removeItem(key: string): void {
      if (!isTabLocal(key)) {
        inner.removeItem(key)
        return
      }
      ssRemove(storageKey(key))
      publishLocal(key, undefined)
    },

    removeItems(keyPrefix: string): void {
      inner.removeItems(keyPrefix)
      // Also clear any tab-local keys that fall under the requested prefix.
      tabLocalKeys.forEach((key) => {
        if (key.startsWith(keyPrefix)) {
          ssRemove(storageKey(key))
          publishLocal(key, undefined)
        }
      })
    },

    reset(): void {
      inner.reset()
      tabLocalKeys.forEach((key) => {
        ssRemove(storageKey(key))
        publishLocal(key, undefined)
      })
    },

    subscribe(key: string, callback: (value: any) => void): () => void {
      if (!isTabLocal(key)) return inner.subscribe(key, callback)
      const id = Math.random().toString()
      subscriptions[id] = { key, callback }
      return () => {
        delete subscriptions[id]
      }
    },
  }
}
